import { faBuilding, faMap } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { createColumnHelper } from "@tanstack/react-table";
import { useDebounce, useDocumentTitle } from "@uidotdev/usehooks";
import { useAtom } from "jotai";
import { useResetAtom } from "jotai/utils";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Site } from "../../../apiClient/generated";
import { useInfrastructureApiClient } from "../../../hooks";
import { ROUTES } from "../../../routes";
import { CoordinatesField } from "../../../ui/GenericFields";
import { SearchInput } from "../../../ui/Inputs";
import { formatDate } from "../../../utils/dates";
import {
    ClearFiltersButton,
    SEARCH_FILTER_KEY,
    useActiveFilters,
} from "../../DataTable/ClearFiltersButton";
import { DataTableV3, RowActionButtons } from "../../DataTable/DataTableV3";
import { TableSettingsModal, TableSortModal } from "../../DataTable/Extra";
import { tableStateFamily } from "../../DataTable/state";
import {
    ExternalProviderFilter,
    LocationFilterV2,
    SiteEmissionThresholdFilter,
    SiteTypeFilter,
} from "../../DataTable/TableFilters";
import { configurableColumns } from "../../InfrastructureDataTable/constants";
import { siteEmissionSortFieldAtom } from "./Sites.atoms";

const columnHelper = createColumnHelper<Site>();

export const SiteApiDataTable = () => {
    useDocumentTitle("Sites - AERSHED");
    const apiClient = useInfrastructureApiClient();
    const [filterSearch, setFilterSearch] = useState("");
    const debouncedSearch = useDebounce(filterSearch, 600);
    const navigate = useNavigate();

    // Table state
    const tableAtom = useMemo(
        () =>
            tableStateFamily({
                tableId: "sites",
                initialState: {
                    filters: {
                        emissionStats: {
                            type: "emissionStats",
                            value: {
                                annotate: true,
                            },
                        },
                    },
                    columnVisibility: {
                        subpartWSegment: false,
                        infraStatus: false,
                        dateOfInstallation: false,
                    },
                },
            }),
        [],
    );
    const [tableState, setTableState] = useAtom(tableAtom);
    const resetTableState = useResetAtom(tableAtom);
    const [emissionSortField] = useAtom(siteEmissionSortFieldAtom);

    const fetchFunction = async (props: any) => {
        return await apiClient.infrastructureSitesList(props);
    };

    const columns = useMemo(
        () => [
            columnHelper.accessor("siteName", {
                id: "siteName",
                cell: (info) => {
                    const value = info.getValue();
                    return (
                        <div
                            className="max-w-52 overflow-hidden truncate"
                            title={value}
                        >
                            {value}
                        </div>
                    );
                },
                header: () => "Name",
            }),
            columnHelper.accessor("siteTypeFromImagery", {
                id: "siteType",
                cell: (info) => {
                    const value = info.getValue() || "-";
                    return (
                        <div className="overflow-hidden truncate capitalize">
                            {value.replaceAll("_", " ")}
                        </div>
                    );
                },
                header: () => "Type (from imagery)",
                meta: {
                    customFilterWidget: SiteTypeFilter,
                    sortingKey: "site_type_from_imagery",
                },
            }),
            columnHelper.accessor("subpartWSegment", {
                id: "subpartWSegment",
                cell: (info) => (
                    <div className="overflow-hidden truncate">
                        {info.getValue()}
                    </div>
                ),
                header: () => "Subject to",
            }),
            columnHelper.accessor(
                (row) => ({
                    operatorUniqueId: row.operatorUniqueId,
                    otherOperatorIds: row.otherOperatorIds,
                }),
                {
                    id: "operatorUniqueIds",
                    header: () => "Operator IDs",
                    cell: (info) => {
                        const value = info.getValue();
                        let opIds = "";
                        if (value.operatorUniqueId.trim()) {
                            opIds = value.operatorUniqueId;
                        }
                        if (value.otherOperatorIds.trim()) {
                            opIds += value.otherOperatorIds
                                .split("¦")
                                .join(", ");
                        }
                        if (opIds.trim()) {
                            return opIds;
                        }
                        return "-";
                    },
                    enableSorting: false,
                    enableColumnFilter: false,
                },
            ),
            columnHelper.accessor("location", {
                id: "location",
                header: () => "Location",
                cell: (info) => {
                    const value = info.getValue();
                    if (value) {
                        return (
                            <CoordinatesField
                                coordinates={value.coordinates}
                                allowCopy={true}
                                mapLinkParams={{
                                    siteId: info.row.original.id,
                                    lat: value.coordinates[1].toString(),
                                    lon: value.coordinates[0].toString(),
                                }}
                            />
                        );
                    }
                    return "-";
                },
                meta: {
                    customFilterWidget: LocationFilterV2,
                },
                enableSorting: false,
            }),
            columnHelper.accessor("sensorConfig", {
                id: "hasExternalProvider",
                header: () => "Onsite Sensors",
                cell: (info) => {
                    const config = info.getValue();
                    const providers: string[] = [];
                    if (config?.longpath_site_id) {
                        providers.push("Longpath");
                    }
                    if (config?.sensirion_site_id) {
                        providers.push("Sensirion");
                    }
                    return providers.length > 0 ? providers.join(", ") : "-";
                },
                meta: {
                    customFilterWidget: ExternalProviderFilter,
                },
                enableSorting: false,
            }),
            columnHelper.accessor("dateOfInstallation", {
                id: "dateOfInstallation",
                cell: (info) => {
                    const value = info.getValue();
                    return formatDate(value);
                },
                header: () => "Installation Date",
                enableColumnFilter: false,
                enableSorting: false,
            }),
            columnHelper.accessor(
                (row) => ({
                    sum: row.emissionSum,
                    count: row.emissionCount,
                }),
                {
                    id: "emissionStats",
                    cell: (info) => {
                        const value = info.getValue();
                        if (value.count === 0) {
                            return "No";
                        }

                        return (
                            <div>
                                <p className="text-sm">Yes</p>
                                <span className="text-xs text-neutral-500">
                                    {`${((value.sum ?? 0) / 1000).toFixed(0)} kg/h total, ${value.count ?? 0} emissions`}
                                </span>
                            </div>
                        );
                    },
                    header: () => "Has emissions?",
                    meta: {
                        customFilterWidget: SiteEmissionThresholdFilter,
                        sortingKey: emissionSortField,
                    },
                    enableColumnFilter: true,
                    enableSorting: true,
                    sortDescFirst: true,
                },
            ),
            columnHelper.display({
                id: "actions",
                header: "",
                enableSorting: false,
                cell: (props) => {
                    const actions = [
                        {
                            fn: () => {
                                window.open(
                                    `${ROUTES.INFRASTRUCTURE_SITE}?site=${props.row.original.id}`,
                                    "_blank",
                                );
                            },
                            icon: (
                                <FontAwesomeIcon
                                    icon={faBuilding}
                                    className="w-3"
                                />
                            ),
                            tooltip: "View site page",
                        },
                        {
                            fn: () => {
                                window.open(
                                    `${ROUTES.MAP}?lat=${props.row.original.location.coordinates[1]}&lon=${props.row.original.location.coordinates[0]}`,
                                    "_blank",
                                );
                            },
                            icon: (
                                <FontAwesomeIcon icon={faMap} className="w-3" />
                            ),
                            tooltip: "Open map",
                        },
                    ];

                    return <RowActionButtons actions={actions} />;
                },
            }),
        ],
        [emissionSortField, navigate],
    );

    const activeFilters = useActiveFilters({
        columns,
        filters: tableState.filters,
        // emissionStats is a seeded annotate flag that powers the "Has emissions?"
        // column data, not a user filter — never show it as a clearable chip.
        ignoreKeys: ["emissionStats"],
        search: filterSearch,
    });

    // Clear a single filter, mirroring resetTableState but for one key.
    const clearFilter = useCallback(
        (key: string) => {
            if (key === SEARCH_FILTER_KEY) {
                setFilterSearch("");
                return;
            }
            setTableState((ts) => ({
                ...ts,
                filters: { ...ts.filters, [key]: undefined },
                pagination: { ...ts.pagination, pageIndex: 0 },
            }));
        },
        [setTableState],
    );

    return (
        <div className="flex-1 flex flex-col">
            {/* Search bar */}
            <div className="py-3 px-4 flex items-center text-sm justify-between border-b">
                <SearchInput
                    placeholder="Search by name, type, facility..."
                    value={filterSearch}
                    onChange={(newValue) => setFilterSearch(newValue)}
                    className="w-96"
                />
                <div className="flex-grow" />
                <div className="flex gap-2 items-center">
                    <ClearFiltersButton
                        activeFilters={activeFilters}
                        onClearFilter={clearFilter}
                        onClearAll={resetTableState}
                    />
                    <TableSortModal<Site>
                        columns={columns}
                        sortingState={tableState.sorting}
                        setSortingState={(newSorting) =>
                            setTableState((ts) => {
                                return {
                                    ...ts,
                                    sorting: newSorting,
                                };
                            })
                        }
                    />
                    <TableSettingsModal
                        columnVisibility={tableState.columnVisibility}
                        setColumnVisibility={(newStates) => {
                            setTableState((ts) => {
                                return {
                                    ...ts,
                                    columnVisibility: newStates,
                                };
                            });
                        }}
                        configurableColumns={configurableColumns}
                    />
                </div>
            </div>

            {/* Data table */}
            <DataTableV3<Site>
                dataName="sites"
                columns={columns}
                extraFilters={{
                    search: debouncedSearch,
                }}
                fetchFunction={fetchFunction}
                sortable={true}
                filterable={true}
            />
        </div>
    );
};
